import { env } from './env';

/**
 * Errors to Sentry and events to Mixpanel, as the web app sends them
 * (`errors-tunnel-through-the-origin`, `analytics-report-to-mixpanel`): the same
 * projects, through the web Worker's tunnels, which answer this origin
 * (`services/auth/src/admin-origins.ts`), and every event tagged `app: admin`. Each is
 * off with no DSN or token — the rig, a laptop — and its SDK is never fetched.
 */
export async function startTelemetry(): Promise<void> {
  const tasks: Promise<unknown>[] = [];
  if (env.VITE_SENTRY_DSN) {
    tasks.push(
      import('@sentry/react').then((Sentry) => {
        Sentry.init({
          dsn: env.VITE_SENTRY_DSN,
          tunnel: `${env.VITE_WEB_ORIGIN}/api/monitoring`,
          environment: env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
          release: env.VITE_APP_VERSION ?? 'dev',
          initialScope: { tags: { app: 'admin' } },
        });
      }),
    );
  }
  if (env.VITE_MIXPANEL_TOKEN) {
    const token = env.VITE_MIXPANEL_TOKEN;
    tasks.push(
      import('mixpanel-browser/dist/mixpanel-core.cjs.js').then(
        ({ default: mixpanel }) => {
          mixpanel.init(token, {
            api_host: `${env.VITE_WEB_ORIGIN}/api/analytics`,
            /* The tunnel's routes, never the vendor's (MathGaps/schools `apps/web/src/lib/analytics`). */
            api_routes: {
              track: 'events/',
              engage: 'people/',
              groups: 'groups/',
            },
            persistence: 'localStorage',
            track_pageview: false,
            autocapture: false,
            record_sessions_percent: 0,
            ignore_dnt: false,
            batch_requests: true,
            property_blacklist: ['$current_url', '$referrer'],
          });
          mixpanel.register({ app: 'admin' });
          analytics = mixpanel;
        },
      ),
    );
  }
  await Promise.allSettled(tasks);
}

let analytics:
  | { identify(id: string): void; track(event: string, props: object): void }
  | undefined;

/** Who the events and errors are from, once signed in. */
export async function identifyTelemetry(uid: string): Promise<void> {
  analytics?.identify(uid);
  if (env.VITE_SENTRY_DSN) {
    const Sentry = await import('@sentry/react');
    Sentry.setUser({ id: uid });
  }
}

/** A boundary's catch, tagged with what caught it (MathGaps/schools `lib/errors`'s `reportError`). */
export async function reportError(
  error: unknown,
  {
    region,
    componentStack,
  }: { region: string; componentStack?: string | null },
): Promise<void> {
  if (!env.VITE_SENTRY_DSN) return;
  const Sentry = await import('@sentry/react');
  Sentry.captureException(error, {
    tags: { region },
    contexts: { react: { componentStack: componentStack ?? null } },
  });
}

/** A tool opened: its id, never the address (an address carries ids). */
export function trackScreen(screen: string): void {
  analytics?.track('Page Viewed', { screen });
}
